import { useEffect, useRef, useState } from 'react';

/**
 * Subscribe to /ws/policy and surface live policy version + recent change
 * events. Reconnects automatically with exponential backoff on disconnect.
 *
 * Returns:
 *   { connected, version, lastChangeAt, lastChangeBy, lastChangeSummary, recent[] }
 *
 * `recent` is a small in-memory ring buffer (≤ 20) of incoming policy.changed
 * events for the change-feed widget on the Policy page.
 */
export function usePolicyStream({ enabled = true } = {}) {
  const [state, setState] = useState({
    connected: false,
    version: null,
    lastChangeAt: null,
    lastChangeBy: null,
    lastChangeSummary: null,
    recent: [],
  });
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const backoffMs = useRef(1000);
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;
    stoppedRef.current = false;

    function open() {
      if (stoppedRef.current) return;
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const url = `${proto}//${window.location.host}/ws/policy`;
      let ws;
      try {
        ws = new WebSocket(url);
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        backoffMs.current = 1000;
        setState((s) => ({ ...s, connected: true }));
      };
      ws.onmessage = (e) => {
        let msg;
        try { msg = JSON.parse(e.data); } catch { return; }
        if (msg.type === 'hello') {
          setState((s) => ({
            ...s,
            version: msg.version ?? 0,
            lastChangeAt: msg.lastChangeAt ?? s.lastChangeAt,
          }));
        } else if (msg.type === 'policy.changed') {
          setState((s) => ({
            ...s,
            version: msg.version,
            lastChangeAt: msg.changedAt,
            lastChangeBy: msg.changedBy,
            lastChangeSummary: msg.summary,
            recent: [
              { ...msg, receivedAt: new Date().toISOString() },
              ...s.recent,
            ].slice(0, 20),
          }));
        } else if (msg.type === 'ping') {
          try { ws.send(JSON.stringify({ type: 'pong' })); } catch { /* ignore */ }
        }
      };
      ws.onclose = () => {
        setState((s) => ({ ...s, connected: false }));
        wsRef.current = null;
        scheduleReconnect();
      };
      ws.onerror = () => {
        try { ws.close(); } catch { /* ignore */ }
      };
    }

    function scheduleReconnect() {
      if (stoppedRef.current) return;
      const delay = backoffMs.current;
      backoffMs.current = Math.min(backoffMs.current * 2, 15_000);
      reconnectTimer.current = setTimeout(open, delay);
    }

    open();

    return () => {
      stoppedRef.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      const ws = wsRef.current;
      if (ws) {
        try { ws.close(1000, 'unmount'); } catch { /* ignore */ }
      }
      wsRef.current = null;
    };
  }, [enabled]);

  return state;
}
